import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { IOrder } from '@/interfaces/order';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { getFullAddress } from '@/utils/customerUtils';
import Title from '@/components/title';
import { IBillingNote, IBillingNoteItem } from '@/interfaces/billing-note';
import { getOrder } from '@/adapters/order';
import { deleteBillingNote, getBillingNote } from '@/adapters/billing-note';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Divider,
  notification,
  Table,
  Tag,
  type TableProps,
} from 'antd';
import { EditOutlined, PrinterOutlined } from '@ant-design/icons';
import { isFullAccess } from '@/utils/permissionUtils';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import PrintBillingNoteModal from '@/components/templates/print-billing-note-modal';

const _billingNoteModal: IDataModal<IBillingNote> = {
  open: false,
};

const _printModal: IDataModal<IBillingNote> = {
  open: false,
};

const ViewBillingNote = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { data: billingNote, refetch } = useQuery<IBillingNote, Error>({
    queryKey: ['get-billing-note', id],
    queryFn: async () => await getBillingNote(Number(id)),
  });

  const firstOrderId = billingNote?.billingNoteItems?.[0]?.orderId;
  const { data: order } = useQuery<IOrder, Error>({
    queryKey: ['get-order', firstOrderId],
    queryFn: async () => await getOrder(Number(firstOrderId)),
    enabled: !!firstOrderId,
  });

  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IBillingNote>>(_billingNoteModal);
  const [print, setPrint] = useState<IDataModal<IBillingNote>>({
    ..._printModal,
  });

  const onDeleteBillingNote = async () => {
    try {
      await deleteBillingNote(Number(deleteModal?.id));
      notification.success({
        message: 'ยกเลิกใบวางบิลสำเร็จ',
      });
      refetch();
      setDeleteModal({ ..._billingNoteModal });
    } catch (error) {
      console.log(error);
    }
  };

  const itemColumns: TableProps<Partial<IBillingNoteItem>>['columns'] = [
    {
      title: 'No.',
      key: 'no',
      align: 'center',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'เลขที่ออเดอร์ (OR)',
      dataIndex: 'orderCode',
      key: 'orderCode',
      className: 'font-bold text-blue-700',
      align: 'center',
      width: 130,
      render: (_, rec) => (
        <div
          onClick={() => window.open(`/order/${rec.orderId}`, '_blank')}
          className="text-blue-700 underline cursor-pointer underline-offset-2"
        >
          {_}
        </div>
      ),
    },
    {
      title: 'เลขที่ใบกำกับ (IV)',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 130,
    },
    {
      title: 'CO No.',
      dataIndex: 'soNo',
      key: 'soNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'PO No.',
      dataIndex: 'poNo',
      key: 'poNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'วันที่',
      dataIndex: 'orderDate',
      key: 'orderDate',
      align: 'center',
      width: 110,
      render: _ => formatDate(_),
    },
    {
      title: 'ครบกำหนด',
      dataIndex: 'dueDate',
      key: 'dueDate',
      align: 'center',
      width: 110,
      render: _ => formatDate(_),
    },
    {
      title: 'จำนวนเงิน',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 120,
      className: 'font-bold',
      render: (_: number) => formatNumber(_ || 0, 2),
    },
    {
      title: 'ชำระแล้ว',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      align: 'right',
      width: 120,
      render: (_: number) => formatNumber(_ || 0, 2),
    },
    {
      title: 'เงินคงค้าง',
      dataIndex: 'balanceAmount',
      key: 'balanceAmount',
      align: 'right',
      width: 120,
      className: 'font-bold text-blue-700',
      render: (_: number, rec) =>
        formatNumber(Number(rec.amount || 0) - Number(rec.paidAmount || 0), 2),
    },
  ];

  const items = billingNote?.billingNoteItems || [];
  const totalPaid = items.reduce(
    (sum, i) => sum + Number(i.paidAmount || 0),
    0,
  );
  const totalBalance = items.reduce(
    (sum, i) => sum + (Number(i.amount || 0) - Number(i.paidAmount || 0)),
    0,
  );

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/billing-note`)}
              >
                ใบวางบิล
              </div>
            ),
          },
          {
            title: `${billingNote?.code || ''}`,
          },
        ]}
      />
      {billingNote && (
        <>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`ใบวางบิล`}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
            <DetailItem label="เลขที่ใบวางบิล" detail={billingNote?.code} />
            <DetailItem
              label="วันที่วางบิล"
              detail={formatDate(billingNote?.docDate)}
            />
            <DetailItem
              label="สถานะ"
              detail={
                <Tag
                  color={billingNote?.isActive ? 'green' : 'red'}
                  className="min-w-[60px] text-center"
                >
                  {billingNote?.isActive ? 'สำเร็จ' : 'ยกเลิก'}
                </Tag>
              }
            />
            <DetailItem
              label="รหัสลูกค้า"
              customClass="text-blue-700"
              detail={billingNote?.customerCode}
            />
            <DetailItem
              label="ลูกค้า"
              customClass="text-blue-700"
              detail={billingNote?.customerName}
            />
            <DetailItem label="BU" detail={billingNote?.businessUnitName} />
            <DetailItem
              label="ที่อยู่ลูกค้า"
              detail={getFullAddress(billingNote?.customer)}
            />
            <DetailItem
              label="ยอดรวมทั้งสิ้น"
              customClass="text-blue-700 font-bold"
              detail={formatNumber(billingNote?.totalAmount || 0, 2)}
            />
            <DetailItem label="ชำระแล้ว" detail={formatNumber(totalPaid, 2)} />
            <DetailItem
              label="เงินคงค้าง"
              customClass="text-blue-700 font-bold"
              detail={formatNumber(totalBalance, 2)}
            />
            <DetailItem label="ผู้สร้าง" detail={billingNote?.createByName} />
            <DetailItem label="หมายเหตุ" detail={billingNote?.remark} />
            {!billingNote?.isActive && (
              <DetailItem
                label="ยกเลิกโดย"
                detail={`${billingNote?.deleteByName || ''} (${formatDate(billingNote?.deleteDate)})`}
                customClass="bg-red-500 text-white p-0.5 rounded px-2"
              />
            )}
          </div>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการวางบิล (${billingNote?.billingNoteItems?.length || 0} รายการ)`}
          />
          <Table<Partial<IBillingNoteItem>>
            size="small"
            bordered
            className="billing-note-items-table"
            rowKey={rec => Number(rec.id)}
            dataSource={billingNote?.billingNoteItems || []}
            columns={itemColumns}
            pagination={false}
            scroll={{ x: 1300 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell
                    index={0}
                    colSpan={7}
                    className="font-bold"
                    align="right"
                  >
                    รวมเงินทั้งสิ้น
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={7}
                    colSpan={1}
                    className="font-bold"
                    align="right"
                  >
                    {formatNumber(billingNote?.totalAmount || 0, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={8}
                    colSpan={1}
                    className="font-bold"
                    align="right"
                  >
                    {formatNumber(totalPaid, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={9}
                    colSpan={1}
                    className="font-bold text-blue-700"
                    align="right"
                  >
                    {formatNumber(totalBalance, 2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <Divider className="" />
          <div className="flex flex-row justify-between">
            <Button
              type="default"
              size="large"
              className="border-red-500 text-red-700"
              disabled={!billingNote?.isActive}
              onClick={() =>
                setDeleteModal({
                  open: true,
                  item: billingNote,
                  id: billingNote?.id,
                })
              }
            >
              ยกเลิกเอกสาร
            </Button>
            <div className="flex flex-row gap-2">
              {billingNote?.isActive && isFullAccess() && (
                <Button
                  type="default"
                  size="large"
                  onClick={() =>
                    navigate(`/billing-note/${billingNote?.id}/edit`)
                  }
                >
                  <EditOutlined />
                  แก้ไข
                </Button>
              )}
              <Button
                type="primary"
                size="large"
                onClick={() =>
                  setPrint({
                    open: true,
                    id: 'TM31',
                  })
                }
              >
                <PrinterOutlined />
                พิมพ์เอกสาร
              </Button>
            </div>
          </div>
          <Suspense>
            <DeleteConfirmModal
              content={`คุณต้องการยกเลิก '${deleteModal?.item?.code}' ใช่หรือไม่`}
              handleCancel={() => {
                setDeleteModal({ ..._billingNoteModal });
              }}
              handleOk={() => {
                onDeleteBillingNote();
              }}
              open={deleteModal?.open}
              title={`ใบวางบิล '${deleteModal?.item?.code}'`}
            />
            <PrintBillingNoteModal
              open={print.open}
              handleCancel={function (): void {
                setPrint({
                  ..._printModal,
                });
              }}
              billingNote={billingNote}
              order={order}
            />
          </Suspense>
        </>
      )}
    </Card>
  );
};

export default ViewBillingNote;
